import { useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, CSSProperties } from 'react';
import Modal from './ui/Modal';
import { IconX, IconChevronDown, IconChevronRight, IconFileText, IconPower } from '@tabler/icons-react';
import Button from './ui/Button';
import { FullPdfViewerLazy } from './ui/FullPDFViewerLazy';
import { Event, KenmerkenschemaDimensionId, KenmerkenschemaSnapshot, PagerSnapshot, PagerUnit } from '../types';
import BACKEND_URL from '../config';
import './ActionView.css';

type Tab = 'incident' | 'kladblok' | 'waterwinning' | 'cocommander';

interface DimensionMeta {
    id: KenmerkenschemaDimensionId;
    icon: string;
    cssClass: string;
    title: string;
}

const DIMENSIONS: DimensionMeta[] = [
    { id: 'mens', icon: 'M', cssClass: 'mens', title: 'Mens' },
    { id: 'gebouw', icon: 'G', cssClass: 'gebouw', title: 'Gebouw' },
    { id: 'brand', icon: 'B', cssClass: 'brand', title: 'Brand' },
    { id: 'omgeving', icon: 'O', cssClass: 'omg', title: 'Omgeving' },
    { id: 'interventie', icon: 'I', cssClass: 'interv', title: 'Interventie' },
];

const formatClassificationChain = (mc: PagerSnapshot['meldingsclassificatie'] | undefined | null): string[] => {
    if (!mc) return [];
    if (mc.chain && mc.chain.length > 0) return mc.chain;
    if (mc.raw) return [mc.raw];
    return [];
};

const formatHeaderTitle = (chain: string[]): string => {
    if (chain.length === 0) return 'Incident';
    if (chain.length === 1) return chain[0];
    return `${chain[0]} - ${chain[1]}`;
};

const formatAddressLines = (address: string | null | undefined): string[] => {
    if (!address) return [];
    const trimmed = address.trim();
    if (!trimmed) return [];
    const commaIdx = trimmed.indexOf(',');
    if (commaIdx === -1) return [trimmed];
    return [trimmed.slice(0, commaIdx + 1), trimmed.slice(commaIdx + 1).trim()].filter(Boolean);
};

const formatLeadUnit = (unit: PagerUnit | undefined): string | null => {
    if (!unit) return null;
    return [unit.eenheid_type, unit.eenheid, unit.kazerne].filter(Boolean).join(' · ') || null;
};

const ActionView = ({
    showActionView,
    setShowActionView,
    eventLog,
    simulationToken,
    createMessage,
    coCommanderKilled,
    setCoCommanderKilled,
    kenmerkenschema,
    pager,
}: {
    showActionView: boolean,
    setShowActionView: (show: boolean) => void,
    eventLog: Event[],
    setEventLog: (eventLog: Event[] | ((prev: Event[]) => Event[])) => void,
    summary: string,
    simulationToken: string | null,
    createMessage: (message: string) => Promise<boolean>,
    coCommanderKilled: boolean,
    setCoCommanderKilled: (v: boolean) => void,
    kenmerkenschema?: KenmerkenschemaSnapshot | null,
    pager?: PagerSnapshot | null,
}) => {
    const [activeTab, setActiveTab] = useState<Tab>('incident');
    const [expandedDocs, setExpandedDocs] = useState<Record<string, boolean>>({});
    const [showDocModal, setShowDocModal] = useState(false);
    const [currentDocUrl, setCurrentDocUrl] = useState<string>('');
    const [docUrlLoading, setDocUrlLoading] = useState(false);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const kladblokEndRef = useRef<HTMLDivElement>(null);

    // Uittrek-handle: kenmerken-paneel breder slepen over de kaart (alleen op Co-Commander-tab).
    const DEFAULT_PANEL = 340;
    const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL);
    const appRef = useRef<HTMLDivElement>(null);
    const resizeStartXRef = useRef(0);
    const resizeStartWRef = useRef(DEFAULT_PANEL);
    const onResizePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
        e.currentTarget.setPointerCapture?.(e.pointerId);
        resizeStartXRef.current = e.clientX;
        resizeStartWRef.current = panelWidth;
    };
    const onResizePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
        if (!e.currentTarget.hasPointerCapture?.(e.pointerId)) return;
        const appW = appRef.current?.clientWidth ?? 0;
        const maxW = Math.max(DEFAULT_PANEL, appW - 56 - 140);
        const next = Math.min(maxW, Math.max(DEFAULT_PANEL, resizeStartWRef.current + (resizeStartXRef.current - e.clientX)));
        setPanelWidth(next);
    };
    // Bij uitschuiven reflowen de kaarten naar 2/3 kolommen zodat alle 5 in beeld blijven
    // (fill-height, geen scroll); de tekst groeit mee maar blijft passen.
    const ccCols = panelWidth < 480 ? 1 : panelWidth < 760 ? 2 : 3;
    const ccRows = Math.ceil(5 / ccCols);
    const ccScale = 1 + (ccCols - 1) * 0.25;

    // Verborgen killswitch-gebaar: Co-Commander-tab 2s ingedrukt houden, dan omhoog swipen.
    const HOLD_MS = 2000;
    const SWIPE_UP_PX = 40;
    const KILL_LINGER_MS = 4000;
    const holdTimerRef = useRef<number | null>(null);
    const lingerTimerRef = useRef<number | null>(null);
    const gestureStartYRef = useRef(0);
    const longPressFiredRef = useRef(false);
    const [killArmed, setKillArmed] = useState(false);

    const clearHoldTimer = () => {
        if (holdTimerRef.current !== null) {
            window.clearTimeout(holdTimerRef.current);
            holdTimerRef.current = null;
        }
    };
    const clearLingerTimer = () => {
        if (lingerTimerRef.current !== null) {
            window.clearTimeout(lingerTimerRef.current);
            lingerTimerRef.current = null;
        }
    };
    const toggleKill = () => {
        clearHoldTimer();
        clearLingerTimer();
        setCoCommanderKilled(!coCommanderKilled);
        setKillArmed(false);
    };
    const onCcPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
        e.currentTarget.setPointerCapture?.(e.pointerId);
        gestureStartYRef.current = e.clientY;
        longPressFiredRef.current = false;
        clearLingerTimer();
        clearHoldTimer();
        holdTimerRef.current = window.setTimeout(() => {
            longPressFiredRef.current = true;
            setKillArmed(true);
        }, HOLD_MS);
    };
    const onCcPointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
        if (killArmed && gestureStartYRef.current - e.clientY > SWIPE_UP_PX) {
            toggleKill();
        }
    };
    const onCcPointerEnd = () => {
        clearHoldTimer();
        // Eenmaal onthuld (2s gehaald): nog even in beeld laten zodat je 'm op laptop kunt aanklikken.
        if (killArmed) {
            clearLingerTimer();
            lingerTimerRef.current = window.setTimeout(() => setKillArmed(false), KILL_LINGER_MS);
        } else {
            setKillArmed(false);
        }
    };
    const onCcClick = () => {
        if (longPressFiredRef.current) {
            longPressFiredRef.current = false;
            return;
        }
        setActiveTab('cocommander');
    };

    const requestSummary = async () => {
        if (!simulationToken || summaryLoading) return;
        setSummaryLoading(true);
        await createMessage('SAI');
        setSummaryLoading(false);
    };

    const kladblokEntries = eventLog.filter(
        (e) => ['ai_operator', 'human_operator'].includes(e.message_sender)
            && !(coCommanderKilled && e.message_sender === 'ai_operator')
    );

    const classificationChain = formatClassificationChain(pager?.meldingsclassificatie);
    const headerTitle = formatHeaderTitle(classificationChain);
    const addressLines = formatAddressLines(pager?.address ?? null);
    const headerSub = pager?.address ?? '';
    const incidentTimestamp = eventLog[0]?.adjusted_start_time ?? null;
    const units = pager?.units ?? [];
    const leadUnitText = formatLeadUnit(units[0]);

    useEffect(() => {
        kladblokEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [eventLog, activeTab]);

    useEffect(() => {
        if (showActionView) setActiveTab('incident');
    }, [showActionView]);

    const toggleDoc = (key: string) => {
        setExpandedDocs((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const openDocByDocumentId = async (documentId: string) => {
        if (!documentId) return;
        setDocUrlLoading(true);
        try {
            const res = await fetch(`${BACKEND_URL}/vnog/documents/${documentId}/presigned_url/`, { credentials: 'include' });
            if (!res.ok) return;
            const data = await res.json();
            if (data.raw_document_presigned_url) {
                setCurrentDocUrl(data.raw_document_presigned_url);
                setShowDocModal(true);
            }
        } finally {
            setDocUrlLoading(false);
        }
    };

    const renderNote = (m: Event, idx: number, scopeKey: string) => {
        const isAI = m.message_sender === 'ai_operator';
        const docTitle = m.other_arguments?.document_title;
        const docId = m.other_arguments?.document_id;
        const quotes = m.other_arguments?.supporting_quotes;
        const key = `${scopeKey}-${idx}`;
        return (
            <div key={key} className={`rpnl-note ${isAI ? 'green' : 'red'}`}>
                <div className="nb">
                    <span className="op-tag">{isAI ? 'AI Operator' : 'Human Operator'}</span>
                    {m.message_content}
                    {isAI && docTitle && (
                        <>
                            <button className="doc-toggle" onClick={() => toggleDoc(key)}>
                                {expandedDocs[key] ? (
                                    <IconChevronDown size={12} style={{ verticalAlign: 'middle' }} />
                                ) : (
                                    <IconChevronRight size={12} style={{ verticalAlign: 'middle' }} />
                                )}{' '}
                                {docTitle}
                            </button>
                            {expandedDocs[key] && (
                                <div style={{ marginTop: 4, paddingLeft: 8, borderLeft: '2px solid rgba(255,255,255,0.4)' }}>
                                    {quotes && (
                                        <div style={{ fontStyle: 'italic', marginBottom: 4 }}>{quotes}</div>
                                    )}
                                    {docId && (
                                        <Button
                                            onClick={() => openDocByDocumentId(docId)}
                                            disabled={docUrlLoading}
                                            className="flex items-center gap-1"
                                        >
                                            <IconFileText size={12} /> {docUrlLoading ? 'Loading...' : 'View Document'}
                                        </Button>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
                <span className="rtime">{m.adjusted_start_time}</span>
            </div>
        );
    };

    const renderCoCommanderCard = (meta: DimensionMeta) => {
        const dim = kenmerkenschema?.dimensions?.[meta.id];
        const facts = dim?.fact_bullets ?? [];
        const insights = dim?.ai_bullets ?? [];
        const isEmpty = facts.length === 0 && insights.length === 0;
        return (
            <div key={meta.id} className="card">
                <div className="top">
                    <div className="title">
                        <span className={`k-icon ${meta.cssClass}`}>{meta.icon}</span>
                        <span className="t-text">{dim?.title || meta.title}</span>
                    </div>
                </div>
                {isEmpty ? (
                    <ul className="fact-bullets">
                        <li style={{ color: '#9a968d', fontStyle: 'italic' }}>Nog niet ingeschat</li>
                    </ul>
                ) : (
                    <>
                        {facts.length > 0 && (
                            <ul className="fact-bullets">
                                {facts.map((b, i) => (
                                    <li key={`f-${i}`}>{b}</li>
                                ))}
                            </ul>
                        )}
                        {insights.length > 0 && (
                            <ul className="ai-bullets">
                                {insights.map((b, i) => (
                                    <li key={`a-${i}`}>{b}</li>
                                ))}
                            </ul>
                        )}
                    </>
                )}
            </div>
        );
    };

    return (
        <>
            <button
                onClick={() => setShowActionView(true)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-sm"
            >
                Actiemodus
            </button>
            <Modal isOpen={showActionView} onRequestClose={() => setShowActionView(false)}>
                <div className="av-root relative">
                    <button
                        onClick={() => setShowActionView(false)}
                        className="av-close"
                        aria-label="Close modal"
                    >
                        <IconX size={20} stroke={1.5} />
                    </button>
                    <div className="av-ipad">
                        <div className="screen">
                            <div className="statusbar">
                                <div className="left">
                                    <span>14:38</span>
                                    <span>ma 20 apr</span>
                                </div>
                                <div className="right">
                                    <svg className="icon-fill" viewBox="0 0 24 24">
                                        <path d="M12 2c-3.5 5-5 8-5 11a5 5 0 0 0 10 0c0-3-1.5-6-5-11z" />
                                        <circle cx="12" cy="13" r="1.6" fill="#000" />
                                    </svg>
                                    <span>5G</span>
                                    <svg className="icon-stroke" viewBox="0 0 24 24">
                                        <path d="M2 9c5.5-5 14.5-5 20 0M5 12.5c4-3.5 10-3.5 14 0M8.5 16c2-2 5-2 7 0" />
                                        <circle cx="12" cy="19" r="0.8" fill="currentColor" />
                                    </svg>
                                    <span>80%</span>
                                </div>
                            </div>
                            <div className="app" ref={appRef}>
                                <div
                                    className="app-left v2-map"
                                    aria-label="MDT-kaart"
                                    style={{ backgroundImage: 'url(/assets/vnog/action_view/map.png)' }}
                                />
                                {activeTab === 'cocommander' && (
                                    <div
                                        className="cc-resize"
                                        style={{ right: panelWidth + 56 }}
                                        onPointerDown={onResizePointerDown}
                                        onPointerMove={onResizePointerMove}
                                        role="separator"
                                        aria-label="Kenmerkenschema breder/smaller slepen"
                                        aria-orientation="vertical"
                                    />
                                )}
                                <aside className="app-right v2-tabs">
                                    <div className="rpnl-head">
                                        <div>
                                            <div className="rpnl-title">{headerTitle}</div>
                                            {headerSub && <div className="rpnl-sub">{headerSub}</div>}
                                        </div>
                                        <div className="rpnl-icons">
                                            <span>⚲</span>
                                            <span>⋮</span>
                                        </div>
                                    </div>

                                    {/* INCIDENT */}
                                    <div className={`panel panel-incident ${activeTab === 'incident' ? 'active' : ''}`}>
                                        <section className="rpnl-section">
                                            <div className="rpnl-shead">
                                                Melding <span className="chev">▾</span>
                                            </div>
                                            <div className="rpnl-sbody">
                                                {incidentTimestamp && (
                                                    <div className="rpnl-row"><span className="ri">🕐</span> {incidentTimestamp}</div>
                                                )}
                                                {classificationChain.length > 0 && (
                                                    <div className="rpnl-row">
                                                        <span className="ri">🔔</span>
                                                        <span>
                                                            {classificationChain.map((seg, i) => (
                                                                <span key={i}>
                                                                    {i > 0 && ' · '}
                                                                    {i === 0 ? seg : <span className="attr-link">{seg}</span>}
                                                                </span>
                                                            ))}
                                                        </span>
                                                    </div>
                                                )}
                                                {addressLines.length > 0 && (
                                                    <div className="rpnl-row">
                                                        <span className="ri">📍</span>
                                                        <span>
                                                            {addressLines.map((line, i) => (
                                                                <span key={i}>
                                                                    {i > 0 && <br />}
                                                                    {line}
                                                                </span>
                                                            ))}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="rpnl-row"><span className="ri">💬</span> {kladblokEntries.length}</div>
                                                <div className="rpnl-row"><span className="ri">🚗</span> {units.length} Voertuig(en)</div>
                                                {leadUnitText && (
                                                    <div className="rpnl-veh">{leadUnitText}</div>
                                                )}
                                            </div>
                                        </section>
                                        <section className="rpnl-section">
                                            <div className="rpnl-sbody">
                                                <Button
                                                    variant="secondary"
                                                    onClick={requestSummary}
                                                    disabled={!simulationToken || summaryLoading}
                                                    loading={summaryLoading}
                                                    className="w-full"
                                                >
                                                    {summaryLoading ? 'Samenvatting ophalen…' : 'Samenvatting'}
                                                </Button>
                                            </div>
                                        </section>
                                        <section className="rpnl-section kladblok-scroll">
                                            <div className="rpnl-shead">
                                                Kladblok <span className="chev">▾</span>
                                            </div>
                                            <div className="rpnl-sbody">
                                                {kladblokEntries.map((m, i) => renderNote(m, i, 'incident'))}
                                                <div ref={activeTab === 'incident' ? kladblokEndRef : null} />
                                            </div>
                                        </section>
                                    </div>

                                    {/* KLADBLOK */}
                                    <div className={`panel panel-kladblok ${activeTab === 'kladblok' ? 'active' : ''}`}>
                                        <section className="rpnl-section">
                                            <div className="rpnl-shead">
                                                Kladblok <span className="chev">▾</span>
                                            </div>
                                            <div className="rpnl-sbody">
                                                {kladblokEntries.map((m, i) => renderNote(m, i, 'kladblok'))}
                                                <div ref={activeTab === 'kladblok' ? kladblokEndRef : null} />
                                            </div>
                                        </section>
                                    </div>

                                    {/* WATERWINNING */}
                                    <div className={`panel panel-waterwinning ${activeTab === 'waterwinning' ? 'active' : ''}`}>
                                        <div className="water-map" aria-label="Waterwinning kaartje">
                                            <svg viewBox="0 0 340 600" preserveAspectRatio="xMidYMid slice">
                                                <rect className="park" x="0" y="430" width="160" height="170" />
                                                <path className="road-edge" d="M0 200 L340 200" />
                                                <path className="road" d="M0 200 L340 200" />
                                                <path className="road-edge" d="M170 0 L170 600" />
                                                <path className="road" d="M170 0 L170 600" />
                                                <path className="road-edge" d="M0 410 L340 410" />
                                                <path className="road" d="M0 410 L340 410" />
                                                <rect className="building" x="20" y="225" width="60" height="50" />
                                                <rect className="building" x="95" y="225" width="60" height="50" />
                                                <rect className="building" x="190" y="225" width="55" height="50" />
                                                <rect className="building" x="260" y="225" width="60" height="50" />
                                                <rect className="building" x="20" y="300" width="50" height="55" />
                                                <rect className="building" x="85" y="300" width="60" height="55" />
                                                <rect className="building" x="195" y="305" width="60" height="50" />
                                                <rect className="building" x="270" y="305" width="50" height="55" />
                                                <rect className="building" x="180" y="445" width="50" height="55" />
                                                <rect className="building" x="250" y="450" width="60" height="60" />
                                                <rect className="building" x="180" y="520" width="80" height="50" />
                                                <line className="water-line" x1="120" y1="245" x2="295" y2="475" />
                                                <rect className="marker-pos-glow" x="105" y="230" width="30" height="30" />
                                                <rect className="marker-pos" x="110" y="235" width="20" height="20" />
                                                <text x="120" y="225" textAnchor="middle" className="marker-label" fill="#1a1a1a">TS</text>
                                                <polygon className="marker-water" points="295,465 285,485 305,485" />
                                                <text x="295" y="458" textAnchor="middle" className="marker-label" fill="#1f5fb3">PUT</text>
                                                <rect className="dist-pill" x="180" y="345" width="60" height="22" rx="3" />
                                                <text className="dist-pill-text" x="210" y="360" textAnchor="middle">120 m</text>
                                            </svg>
                                            <div className="legend">
                                                <div className="lg-row"><span className="lg-pos" /><span>TS 06-4031 (huidige positie)</span></div>
                                                <div className="lg-row"><span className="lg-tri" /><span>Waterput · 120 m</span></div>
                                            </div>
                                        </div>
                                    </div>

                                </aside>

                                {/* CO-COMMANDER: kenmerkenschema als overlay over de statische map */}
                                {activeTab === 'cocommander' && (
                                    <div className={`cc-overlay ${coCommanderKilled ? 'cc-disabled' : ''}`} style={{ width: panelWidth }}>
                                        <div className="rpnl-head">
                                            <div>
                                                <div className="rpnl-title">{headerTitle}</div>
                                                {headerSub && <div className="rpnl-sub">{headerSub}</div>}
                                            </div>
                                            <div className="rpnl-icons"><span>⚲</span><span>⋮</span></div>
                                        </div>
                                        <div
                                            className="cards-vertical-compact"
                                            style={{ gridTemplateColumns: `repeat(${ccCols},1fr)`, gridTemplateRows: `repeat(${ccRows},1fr)`, ['--cc-scale']: ccScale } as CSSProperties}
                                        >
                                            {DIMENSIONS.map(renderCoCommanderCard)}
                                        </div>
                                    </div>
                                )}

                                <nav className="app-tabs">
                                    <button
                                        className={`tab ${activeTab === 'incident' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('incident')}
                                    >
                                        <span className="tico">
                                            <svg viewBox="0 0 24 24"><path d="M3 4h18v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4z" /><path d="M3 9h18" /></svg>
                                        </span>
                                        Incident
                                    </button>
                                    <button
                                        className={`tab ${activeTab === 'kladblok' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('kladblok')}
                                    >
                                        <span className="tico">
                                            <svg viewBox="0 0 24 24"><path d="M4 4h16v13H8l-4 4V4z" /></svg>
                                        </span>
                                        Kladblok
                                    </button>
                                    <button
                                        className={`tab ${activeTab === 'waterwinning' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('waterwinning')}
                                    >
                                        <span className="tico">
                                            <svg viewBox="0 0 24 24"><path d="M12 3c-4 6-6 9-6 12a6 6 0 0 0 12 0c0-3-2-6-6-12z" /></svg>
                                        </span>
                                        Waterwinning
                                    </button>
                                    <div className="cc-tab-wrap">
                                        <button
                                            type="button"
                                            className={`cc-kill ${killArmed ? 'revealed' : ''} ${coCommanderKilled ? 'armed' : ''}`}
                                            onClick={toggleKill}
                                            tabIndex={killArmed ? 0 : -1}
                                            aria-label={coCommanderKilled ? 'Co-Commander weer inschakelen' : 'Co-Commander uitschakelen'}
                                            title={coCommanderKilled ? 'Co-Commander weer inschakelen' : 'Co-Commander uitschakelen'}
                                        >
                                            <IconPower size={24} stroke={1.75} />
                                        </button>
                                        <button
                                            className={`tab ${activeTab === 'cocommander' ? 'active' : ''}`}
                                            onClick={onCcClick}
                                            onPointerDown={onCcPointerDown}
                                            onPointerMove={onCcPointerMove}
                                            onPointerUp={onCcPointerEnd}
                                            onPointerCancel={onCcPointerEnd}
                                        >
                                            <span className="tico">
                                                <svg viewBox="0 0 24 24"><rect x="4" y="4" width="7" height="7" /><rect x="13" y="4" width="7" height="7" /><rect x="4" y="13" width="7" height="7" /><rect x="13" y="13" width="7" height="7" /></svg>
                                            </span>
                                            Co-commander
                                        </button>
                                    </div>
                                </nav>

                                <aside className="sys-bar" aria-label="Kazerne-systeembalk">
                                    <button className="sb-btn sb-menu" aria-label="Menu">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                                            <path d="M4 7h16M4 12h16M4 17h16" />
                                        </svg>
                                    </button>
                                    <div className="sb-veh">4031</div>
                                    <div className="sb-gap" />
                                    <div className="sb-actions">
                                        <button className="sb-btn active" aria-label="Kaart">
                                            <svg viewBox="0 0 24 24"><path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2z" /><path d="M9 4v14M15 6v14" /></svg>
                                        </button>
                                    </div>
                                    <div className="sb-spacer" />
                                </aside>
                            </div>
                        </div>
                        <div className="android-nav">
                            <svg viewBox="0 0 24 24"><rect x="5" y="5" width="14" height="14" rx="1" /></svg>
                            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="6" /></svg>
                            <svg viewBox="0 0 24 24"><path d="M15 5l-7 7 7 7" /></svg>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Document Viewer Modal */}
            <Modal isOpen={showDocModal} onRequestClose={() => setShowDocModal(false)}>
                <div className="w-[90vw] h-[90vh] bg-white rounded-lg flex flex-col">
                    <div className="flex justify-between items-center p-4 border-b">
                        <h2 className="text-xl font-semibold">Document Viewer</h2>
                        <button
                            onClick={() => setShowDocModal(false)}
                            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                        >
                            <IconX size={24} stroke={1.5} />
                        </button>
                    </div>
                    <div className="flex-1 p-4 overflow-y-auto">
                        <FullPdfViewerLazy pdfUrl={currentDocUrl} />
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default ActionView;
